import React, { useState, useMemo } from 'react';
import MqttChart from '../mqtt/MqttChart';
import OverlayChart from '../mqtt/OverlayChart';
import '../../styles/actions.css';

function DistanceThresholdTest() {
    const [isChartOverlay, setIsChartOverlay] = useState(false);

    const mockDistanceData = useMemo(() => {
        const data = [];
        const now = new Date();
        for (let i = 0; i < 24; i++) {
            const timestamp = new Date(now.getTime() - (23 - i) * 60 * 60 * 1000);
            // Simulate water level data that goes above and below thresholds including 0
            const distance = 2 + 1.5 * Math.sin(i * 0.3) + Math.random() * 0.5; // Range roughly -0.5 to 4.5
            data.push({
                timestamp: timestamp.toISOString(),
                distance: Math.max(-0.5, Math.min(4.5, distance)) // Allow negative values to test 0 threshold
            });
        }
        return data;
    }, []);

    const mockIrrigationData = useMemo(() => {
        const data = [];
        const now = new Date();
        for (let i = 0; i < 24; i++) {
            const timestamp = new Date(now.getTime() - (23 - i) * 60 * 60 * 1000);
            // Irrigation more active when water level is low
            const distance = mockDistanceData[i]?.distance || 2;
            const isActive = distance < 1 ? (Math.random() > 0.2 ? 1 : 0) : (Math.random() > 0.9 ? 1 : 0);
            data.push({
                timestamp: timestamp.toISOString(),
                irrigation: isActive
            });
        }
        return data;
    }, [mockDistanceData]);

    // Test different threshold scenarios
    const [thresholdScenario, setThresholdScenario] = useState('normal');
    
    const getThresholds = () => {
        switch (thresholdScenario) {
            case 'zero-high':
                return { low: -0.5, high: 0 }; // High threshold at 0 (should still show!)
            case 'zero-low':
                return { low: 0, high: 3.5 }; // Low threshold at 0 (should still show!)
            case 'both-zero':
                return { low: 0, high: 0 }; // Both at 0 (both should show!)
            case 'normal':
            default:
                return { low: 0.5, high: 3.5 }; // Normal positive thresholds
        }
    };

    const thresholds = getThresholds();

    return (
        <div style={{ padding: '2rem', background: '#1a1a1a', minHeight: '100vh' }}>
            <h1 style={{ color: 'white', textAlign: 'center', marginBottom: '2rem' }}>
                Distance Threshold Test
            </h1>

            <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
                <div style={{ marginBottom: '1rem' }}>
                    <label style={{ color: 'white', marginRight: '1rem' }}>Test Scenario:</label>
                    <select 
                        value={thresholdScenario} 
                        onChange={(e) => setThresholdScenario(e.target.value)}
                        style={{ padding: '5px', borderRadius: '3px' }}
                    >
                        <option value="normal">Normal (Low: 0.5", High: 3.5")</option>
                        <option value="zero-high">Zero High (Low: -0.5", High: 0")</option>
                        <option value="zero-low">Zero Low (Low: 0", High: 3.5")</option>
                        <option value="both-zero">Both Zero (Low: 0", High: 0")</option>
                    </select>
                </div>
                
                <button 
                    onClick={() => setIsChartOverlay(!isChartOverlay)}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: isChartOverlay ? '#4CAF50' : '#333',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer'
                    }}
                >
                    {isChartOverlay ? 'Show Separate Charts' : 'Show Overlay Chart'}
                </button>
            </div>

            <div style={{ 
                background: '#2a2a2a', 
                borderRadius: '10px', 
                padding: '2rem',
                marginBottom: '2rem'
            }}>
                <h2 style={{ color: 'white', textAlign: 'center', marginBottom: '1rem' }}>
                    Water Level (Distance) + Irrigation
                </h2>
                <p style={{ color: 'lightgray', textAlign: 'center', marginBottom: '2rem' }}>
                    Current Thresholds: Low={thresholds.low}" (blue), High={thresholds.high}" (red)
                </p>

                {isChartOverlay ? (
                    <div>
                        <h3 style={{ textAlign: 'center', marginBottom: '1rem', color: 'white' }}>
                            🔗 Overlay Chart
                        </h3>
                        <OverlayChart 
                            sensorType="Distance"
                            actionType="Irrigation"
                            sensorHistoricalData={mockDistanceData}
                            actionHistoricalData={mockIrrigationData}
                            sensorThresholds={thresholds}
                            selectedTimeRange="1d"
                        />
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                        <div>
                            <h3 style={{ textAlign: 'center', marginBottom: '1rem', color: 'white' }}>
                                💧 Water Level (Distance)
                            </h3>
                            <MqttChart 
                                sensorType="Distance"
                                historicalData={mockDistanceData}
                                thresholds={thresholds}
                                selectedTimeRange="1d"
                            />
                        </div>
                        <div>
                            <h3 style={{ textAlign: 'center', marginBottom: '1rem', color: 'white' }}>
                                🚿 Irrigation Status
                            </h3>
                            <MqttChart 
                                sensorType="Irrigation"
                                historicalData={mockIrrigationData}
                                selectedTimeRange="1d"
                            />
                        </div>
                    </div>
                )}
            </div>

            <div style={{ 
                background: '#2a2a2a', 
                borderRadius: '10px', 
                padding: '2rem',
                color: 'lightgray'
            }}>
                <h3 style={{ color: 'white', marginBottom: '1rem' }}>Debug Info:</h3>
                <ul>
                    <li>Distance data points: {mockDistanceData.length}</li>
                    <li>Irrigation data points: {mockIrrigationData.length}</li>
                    <li>Low threshold: {thresholds.low}" ({typeof thresholds.low})</li>
                    <li>High threshold: {thresholds.high}" ({typeof thresholds.high})</li>
                    <li>Data range: {Math.min(...mockDistanceData.map(d => d.distance)).toFixed(2)}" to {Math.max(...mockDistanceData.map(d => d.distance)).toFixed(2)}"</li>
                    <li><strong>Both thresholds should be visible even when set to 0!</strong></li>
                    <li>Check browser console for threshold validation logs</li>
                </ul>
            </div>
        </div>
    );
}

export default DistanceThresholdTest;
